import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Download, FileText, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/components/guards/permission-guard';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { dateStamp } from '@/lib/download-xlsx';
import { exportCariMizanXlsx } from '@/lib/export-cari-mizan-xlsx';
import { CariMizanPDF } from '@/components/travel/cari/cari-mizan-pdf';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import {
  MizanPeriodPicker,
  defaultMizanPeriod,
  mizanPeriodToDates,
} from '@/components/reports/mizan-period-picker';
import {
  selectClass,
  useTravelCariTypes,
  CURRENCIES,
  CURRENCY_NAMES,
  fmtMoney,
  useCariTypeLabel,
  useFiscalYear,
} from '../shared';

// Legacy ProAcenta "Cari Mizan" for the TRAVEL business: per-counterparty
// four-column trial balance (Borç/Alacak turnover + Borç/Alacak Bakiye)
// grouped by currency and cari type, within ONE legacy fiscal year.
export function TravelCariMizanPage() {
  const canExport = usePermission('ACCOUNTING', 'EXPORT', 'TRAVEL');
  const availableTypes = useTravelCariTypes();
  const { t, i18n } = useTranslation('accounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const typeLabel = useCariTypeLabel();
  const { years, fiscalYear, setFiscalYear } = useFiscalYear();

  const [period, setPeriod] = useState(defaultMizanPeriod());
  const [cariTypes, setCariTypes] = useState<string[]>([]);
  const [currency, setCurrency] = useState('');
  const [includeZero, setIncludeZero] = useState(true);
  const [excludeCarryForward, setExcludeCarryForward] = useState(false);
  const [valueMode, setValueMode] = useState<'native' | 'tryEquivalent'>('native');
  const [sortBy, setSortBy] = useState<'code' | 'name'>('code');
  const [exporting, setExporting] = useState(false);

  // Each legacy fiscal year is self-contained — lock the period to it.
  useEffect(() => {
    if (fiscalYear) setPeriod((p) => (p.year === fiscalYear ? p : { ...p, year: fiscalYear }));
  }, [fiscalYear]);

  const { start, end } = useMemo(() => mizanPeriodToDates(period), [period]);

  const queryInput = fiscalYear
    ? {
        fiscalYear,
        startDate: start,
        endDate: end,
        cariTypes: cariTypes.length > 0 ? (cariTypes as (typeof availableTypes)[number][]) : undefined,
        currency: (currency || undefined) as (typeof CURRENCIES)[number] | undefined,
        includeZeroBalances: includeZero,
        excludeOpeningCarryForward: excludeCarryForward,
        valueMode,
        sortBy,
      }
    : undefined;

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.travelCari.cariMizan.useQuery(queryInput!, { enabled: !!queryInput });

  const fmtDayLocal = (d: Date | string) => new Date(d).toLocaleDateString(dateLocale);

  // Shared metadata for the PDF + Excel exports (all labels pre-translated).
  const buildExportMeta = () => ({
    title:
      period.mode === 'range'
        ? t('cari.mizanRangeTitle', 'Date Range Cari Mizan').toLocaleUpperCase('tr-TR')
        : t('cari.mizanTitle', 'Cari Mizan').toLocaleUpperCase('tr-TR'),
    periodLabel:
      (start ? `${fmtDayLocal(start)} - ` : '') + (end ? fmtDayLocal(end) : String(fiscalYear)),
    generatedLabel: t('cari.exportGenerated', 'Generated {{ts}}', {
      ts: new Date().toLocaleString(dateLocale),
    }),
    params: [
      { label: t('cari.fiscalYear', 'Fiscal Year'), value: String(fiscalYear) },
      { label: t('cari.currency', 'Currency'), value: currency || t('cari.all', 'All') },
      {
        label: t('cari.reportType', 'Report Type'),
        value: valueMode === 'native' ? t('cari.nativeMode', 'FX and/or TL mizan') : t('cari.tryMode', 'TL equivalents'),
      },
      {
        label: t('cari.cariTypes', 'Cari Types'),
        value: cariTypes.length > 0 ? cariTypes.join(', ') : t('cari.all', 'All'),
      },
      {
        label: t('cari.includeZero', 'Include zero balances'),
        value: includeZero ? t('cari.exportYes', 'Yes') : t('cari.exportNo', 'No'),
      },
      ...(excludeCarryForward
        ? [{ label: t('cari.excludeCarryForward', 'Exclude prior year-end carryover'), value: t('cari.exportYes', 'Yes') }]
        : []),
    ],
    columns: {
      no: '#',
      code: t('cari.cariNo', 'Cari No'),
      name: t('cari.name', 'Name'),
      debit: t('cari.debit', 'Debit (Borç)'),
      credit: t('cari.credit', 'Credit (Alacak)'),
      debitBalance: t('cari.debitBalance', 'Debit Balance'),
      creditBalance: t('cari.creditBalance', 'Credit Balance'),
    },
    totalLabel: t('cari.total', 'TOTAL'),
    balanceLabel: t('cari.balance', 'BALANCE'),
    debitSide: t('cari.debitSide', '(D)'),
    creditSide: t('cari.creditSide', '(C)'),
    pageLabel: (page: number, total: number) =>
      t('cari.exportPage', 'Page {{page}} / {{total}}', { page, total }),
    currencyNames: CURRENCY_NAMES,
    typeLabel,
  });

  const saveBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onExportPdf = async () => {
    if (!queryInput) return;
    setExporting(true);
    try {
      const res = await utils.travelCari.cariMizan.fetch(queryInput);
      const blob = await pdf(
        <CariMizanPDF
          data={{ fiscalYear: res.fiscalYear, currencies: res.currencies }}
          meta={buildExportMeta()}
          logoUrl="/polatkan-logo.png"
        />
      ).toBlob();
      saveBlob(blob, `travel-cari-mizan-${fiscalYear}-${dateStamp()}.pdf`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const onExportXlsx = async () => {
    if (!queryInput) return;
    setExporting(true);
    try {
      const res = await utils.travelCari.cariMizan.fetch(queryInput);
      await exportCariMizanXlsx(
        { fiscalYear: res.fiscalYear, currencies: res.currencies },
        buildExportMeta(),
        `travel-cari-mizan-${fiscalYear}-${dateStamp()}.xlsx`
      );
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const toggleType = (ct: string) => {
    setCariTypes((prev) => (prev.includes(ct) ? prev.filter((x) => x !== ct) : [...prev, ct]));
  };

  const fmtCell = (n: number) => (Math.abs(n) < 0.005 ? '—' : fmtMoney(n));
  const fmtDay = (d: Date | string) => new Date(d).toLocaleDateString(dateLocale);

  const periodLabel = data
    ? data.startDate
      ? `${fmtDay(data.startDate)} - ${data.endDate ? fmtDay(data.endDate) : ''}`
      : data.endDate
        ? t('cari.asOfValue', 'As of {{date}}', { date: fmtDay(data.endDate) })
        : String(data.fiscalYear)
    : '';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('cari.mizanTitle', 'Cari Mizan')}</h1>
          <p className="text-muted-foreground">
            {t('cari.mizanSubtitle', 'Per-account trial balance: turnover and balance per currency')}
          </p>
        </div>
        {canExport && <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={exporting || !data?.currencies.length}>
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {t('cari.export', 'Export')}
              <ChevronDown className="ml-2 h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExportPdf}>
              <FileText className="h-4 w-4 text-red-600" />
              {t('cari.exportPdf', 'PDF')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportXlsx}>
              <FileSpreadsheet className="h-4 w-4 text-green-600" />
              {t('cari.exportExcel', 'Excel')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>}
      </div>

      {/* Filters */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">{t('cari.fiscalYear', 'Fiscal Year')}</Label>
            <select
              value={fiscalYear ?? ''}
              onChange={(e) => setFiscalYear(Number(e.target.value))}
              className={`${selectClass} block w-24`}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <MizanPeriodPicker
            value={period}
            onChange={setPeriod}
            ns="accounting"
            keyPrefix="cari"
            fixedYear={fiscalYear ?? undefined}
          />
        </div>

        {/* Cari type chips */}
        <div className="space-y-1">
          <Label className="text-xs">
            {t('cari.cariTypes', 'Cari Types')}{' '}
            <span className="text-muted-foreground">({t('cari.allWhenEmpty', 'all when none selected')})</span>
          </Label>
          <div className="flex flex-wrap gap-1">
            {availableTypes.map((ct) => (
              <button
                key={ct}
                type="button"
                onClick={() => toggleType(ct)}
                className={`h-7 border px-2 text-xs transition-colors ${
                  cariTypes.includes(ct)
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background hover:bg-muted'
                }`}
                aria-pressed={cariTypes.includes(ct)}
              >
                {ct} · {typeLabel(ct)}
              </button>
            ))}
          </div>
        </div>

        {/* Value ranges + report type */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">{t('cari.currency', 'Currency')}</Label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${selectClass} block w-28`}>
              <option value="">{t('cari.all', 'All')}</option>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('cari.reportType', 'Report Type')}</Label>
            <select
              value={valueMode}
              onChange={(e) => setValueMode(e.target.value as 'native' | 'tryEquivalent')}
              className={`${selectClass} block w-64`}
            >
              <option value="native">{t('cari.nativeMode', 'FX and/or TL mizan')}</option>
              <option value="tryEquivalent">{t('cari.tryMode', 'TL equivalents of FX transactions')}</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('cari.sort', 'Sort')}</Label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'code' | 'name')} className={`${selectClass} block w-40`}>
              <option value="code">{t('cari.sortByCode', 'By cari no')}</option>
              <option value="name">{t('cari.sortByName', 'By name')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <input
              type="checkbox"
              id="includeZeroTravelMizan"
              checked={includeZero}
              onChange={(e) => setIncludeZero(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="includeZeroTravelMizan">{t('cari.includeZero', 'Include zero balances')}</Label>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <input
              type="checkbox"
              id="excludeCarryForwardTravel"
              checked={excludeCarryForward}
              onChange={(e) => setExcludeCarryForward(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="excludeCarryForwardTravel">
              {t('cari.excludeCarryForward', 'Exclude prior year-end carryover')}
            </Label>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div className="rounded-xl border bg-card">
          <div className="border-b p-4">
            <h3 className="text-lg font-semibold">
              {period.mode === 'range'
                ? t('cari.mizanRangeTitle', 'Date Range Cari Mizan')
                : t('cari.mizanTitle', 'Cari Mizan')}{' '}
              · {data.fiscalYear}
            </h3>
            <p className="text-sm text-muted-foreground">{periodLabel}</p>
          </div>
          {data.currencies.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground">{t('cari.noMovementsPeriod', 'No movements in this period')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-3 text-left font-medium">#</th>
                    <th className="px-3 py-3 text-left font-medium">{t('cari.cariNo', 'Cari No')}</th>
                    <th className="px-3 py-3 text-left font-medium">{t('cari.name', 'Name')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cari.debit', 'Debit (Borç)')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cari.credit', 'Credit (Alacak)')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cari.debitBalance', 'Debit Balance')}</th>
                    <th className="px-3 py-3 text-right font-medium">{t('cari.creditBalance', 'Credit Balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.currencies.map((cur) => (
                    <CurrencySection key={cur.currency} section={cur} typeLabel={typeLabel} fmtCell={fmtCell} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Structural mirror of travelCari.cariMizan's response.
interface MizanRowData {
  cariCode: string;
  companyName: string;
  cariType: string;
  debit: number;
  credit: number;
  debitBalance: number;
  creditBalance: number;
}

interface MizanTotalsData {
  debit: number;
  credit: number;
  debitBalance: number;
  creditBalance: number;
  net: number;
}

interface CariGroupData {
  cariType: string;
  rows: MizanRowData[];
  totals: MizanTotalsData;
}

interface CurrencySectionData {
  currency: string;
  cariGroups: CariGroupData[];
  totals: MizanTotalsData;
}

interface CurrencySectionProps {
  section: CurrencySectionData;
  typeLabel: (ct: string) => string;
  fmtCell: (n: number) => string;
}

function CurrencySection({ section, typeLabel, fmtCell }: CurrencySectionProps) {
  const { t } = useTranslation('accounting');
  let rowNo = 0;
  return (
    <>
      <tr className="border-y bg-muted/70">
        <td colSpan={7} className="px-3 py-2 font-semibold">
          {CURRENCY_NAMES[section.currency] ?? section.currency}
        </td>
      </tr>
      {section.cariGroups.map((group) => (
        <CariGroupRows
          key={group.cariType}
          group={group}
          startNo={(() => {
            const s = rowNo;
            rowNo += group.rows.length;
            return s;
          })()}
          typeLabel={typeLabel}
          fmtCell={fmtCell}
        />
      ))}
      <tr className="border-t-2 bg-muted/50 font-bold">
        <td colSpan={3} className="px-3 py-2.5">
          {section.currency} {t('cari.total', 'TOTAL')}
        </td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtMoney(section.totals.debit)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtMoney(section.totals.credit)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtMoney(section.totals.debitBalance)}</td>
        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtMoney(section.totals.creditBalance)}</td>
      </tr>
      <tr className="border-b bg-muted/30 font-semibold">
        <td colSpan={5} />
        <td colSpan={2} className={`px-3 py-2 text-right ${section.totals.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {t('cari.balance', 'BALANCE')}: {fmtMoney(Math.abs(section.totals.net))}{' '}
          {section.totals.net >= 0 ? t('cari.debitSide', '(B)') : t('cari.creditSide', '(A)')}
        </td>
      </tr>
    </>
  );
}

interface CariGroupRowsProps {
  group: CariGroupData;
  startNo: number;
  typeLabel: (ct: string) => string;
  fmtCell: (n: number) => string;
}

function CariGroupRows({ group, startNo, typeLabel, fmtCell }: CariGroupRowsProps) {
  const { t } = useTranslation('accounting');
  return (
    <>
      <tr className="border-b bg-muted/40">
        <td colSpan={7} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.cariType} - {typeLabel(group.cariType)}
        </td>
      </tr>
      {group.rows.map((row, i) => (
        <tr key={row.cariCode} className="border-b last:border-0 hover:bg-muted/30">
          <td className="px-3 py-2 text-muted-foreground">{startNo + i + 1}</td>
          <td className="px-3 py-2 font-mono text-xs">{row.cariCode}</td>
          <td className="px-3 py-2">{row.companyName}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.debit)}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.credit)}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.debitBalance)}</td>
          <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtCell(row.creditBalance)}</td>
        </tr>
      ))}
      <tr className="border-b bg-muted/20 font-semibold">
        <td colSpan={3} className="px-3 py-2">
          {group.cariType} - {typeLabel(group.cariType)} {t('cari.total', 'TOTAL')}
        </td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(group.totals.debit)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(group.totals.credit)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(group.totals.debitBalance)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(group.totals.creditBalance)}</td>
      </tr>
    </>
  );
}
